import React from "react";
import { withTranslation } from "react-i18next";
import { clearGlobalNetworkError } from "utils/globalNetworkError";
import { capturePostHogException } from "posthog";

const CHUNK_RELOAD_KEY = "chunk_fail_reload";

export function isChunkLoadError(error) {
  if (!error?.message) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes("loading chunk") ||
    msg.includes("chunkloaderror") ||
    msg.includes("loading css chunk") ||
    msg.includes("failed to fetch dynamically imported module")
  );
}

export function isNetworkError(error) {
  if (!error?.message) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes("failed to fetch") ||
    msg.includes("network error") ||
    msg.includes("load failed") ||
    msg.includes("network request failed") ||
    (error.name && error.name.toLowerCase().includes("network"))
  );
}

class ChunkLoadErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Errors a boundary catches never reach window.onerror in production builds,
    // so PostHog's unhandled-error capture would not see them.
    capturePostHogException(error, {
      component_stack: info?.componentStack,
    });
    if (isNetworkError(error)) {
      clearGlobalNetworkError();
    }
    if (isChunkLoadError(error)) {
      const alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_KEY);
      if (!alreadyReloaded) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
        window.location.reload();
        return;
      }
    }
  }

  handleRefresh = () => {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    clearGlobalNetworkError();
    window.location.reload();
  };

  render() {
    const { t } = this.props;
    if (this.state.hasError && this.state.error) {
      const fullScreenFallback = (
        <div
          className="flex flex-col items-center justify-center gap-4 p-6 text-center fixed inset-0 z-[9999] bg-surface"
          style={{ minHeight: "100vh", minWidth: "100vw" }}
          role="alert"
        >
          {isChunkLoadError(this.state.error) && (
            <>
              <h2 className="text-xl font-semibold text-text-body">
                {t("errorBoundary.pageCouldNotBeLoaded")}
              </h2>
              <p className="text-secondary max-w-md">
                {t("errorBoundary.pageCouldNotBeLoadedDesc")}
              </p>
            </>
          )}
          {isNetworkError(this.state.error) && (
            <>
              <h2 className="text-xl font-semibold text-text-body">
                {t("errorBoundary.networkError")}
              </h2>
              <p className="text-secondary max-w-md">
                {t("errorBoundary.networkErrorDesc")}
              </p>
            </>
          )}
          {!isChunkLoadError(this.state.error) &&
            !isNetworkError(this.state.error) && (
              <>
                <h2 className="text-xl font-semibold text-text-body">
                  {t("errorBoundary.somethingWentWrong")}
                </h2>
                <p className="text-secondary max-w-md">
                  {t("errorBoundary.somethingWentWrongDesc")}
                </p>
              </>
            )}
          <button
            type="button"
            onClick={this.handleRefresh}
            className="px-4 py-2 bg-primary text-surface rounded hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {isNetworkError(this.state.error)
              ? t("errorBoundary.tryAgain")
              : t("errorBoundary.refreshPage")}
          </button>
        </div>
      );

      return fullScreenFallback;
    }
    return this.props.children;
  }
}

export default withTranslation()(ChunkLoadErrorBoundary);
